import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, QrCode, Search, Send, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { adminApi, formatApiError } from "@/lib/adminApi";
import { SITE } from "@/data/site";
import { czk } from "@/lib/price";
import { CardScanner } from "@/components/admin/CardScanner";
import { MemberProfile } from "@/components/admin/MemberProfile";
import { RewardsPanel } from "@/components/admin/RewardsPanel";

const API = process.env.REACT_APP_BACKEND_URL;
const inputCls = "bg-brand-bg border-brand-border text-white h-9 text-sm";
const loc = (k) => SITE.locations.find((l) => l.key === k)?.name.replace("Praha ", "") || k;

const SVC_LABEL = { ceramic: "Keramika", "ceramic-3y": "Keramika 3r", leather: "Kůže", "wet-cleaning": "Tepování",
  detailing: "Detailing", signature: "Signature", ozone: "Ozon", "nano-water": "Nano sklo", wipers: "Stěrače" };
const svcLabel = (s) => SVC_LABEL[s] || s;
const SEG = {
  active: { label: "Aktivní kupující", cls: "bg-brand-accent text-black" },
  buyer: { label: "Kupující", cls: "border border-brand-accent text-brand-accent" },
  base: { label: "Nenakupuje", cls: "border border-brand-border text-brand-muted" },
};
const segOf = (l) => SEG[l.segment] || SEG.base;

function Stat({ label, value, sub, accent, testId }) {
  return (
    <div className="bg-brand-surface border border-brand-border p-4" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted">{label}</div>
      <div className={`mt-1.5 font-display font-black text-2xl ${accent ? "text-brand-accent" : ""}`}>{value}</div>
      {sub && <div className="text-xs text-brand-muted mt-1">{sub}</div>}
    </div>
  );
}

function ClubStats() {
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const [s, setS] = useState(null);
  useEffect(() => {
    adminApi.get("/admin/club/stats", { params: { date_from: range.date_from || undefined, date_to: range.date_to || undefined } }).then((r) => setS(r.data)).catch(() => {});
  }, [range]);
  return (
    <div className="space-y-3" data-testid="club-stats">
      <div className="flex items-center gap-2 text-xs text-brand-muted">
        Období: <Input type="date" data-testid="club-stats-from" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} className={`${inputCls} w-40`} />
        – <Input type="date" data-testid="club-stats-to" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} className={`${inputCls} w-40`} />
      </div>
      {s && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <Stat testId="club-members-total" label="Členů celkem" value={`${s.members_total}`} sub={`cíl ${s.members_goal} · ${Math.round((s.members_total / s.members_goal) * 100)} %`} accent />
          <Stat testId="club-members-new" label="Noví členové" value={s.new_members} sub="ve zvoleném období" />
          <Stat testId="club-first-visit" label="S první dokončenou návštěvou" value={s.members_first_visit} />
          <Stat testId="club-repeat" label="Další návštěva do 60 dní" value={s.members_repeat_60d} />
          <Stat testId="club-revenue" label="Uhrazené tržby" value={czk(s.revenue_paid)} sub={`${s.paid_visits} dokončených a uhrazených návštěv (poptávky se nepočítají)`} accent />
        </div>
      )}
    </div>
  );
}

function Members({ onOpen, refreshKey }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  useEffect(() => {
    const t = setTimeout(() => adminApi.get("/admin/club/members", { params: { q } }).then((r) => setItems(r.data)).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q, refreshKey]);
  return (
    <div className="bg-brand-surface border border-brand-border" data-testid="club-members">
      <div className="p-3 flex items-center gap-2 border-b border-brand-border">
        <Search size={14} className="text-brand-muted" />
        <Input data-testid="club-members-search" placeholder="Hledat jméno, e-mail, telefon, číslo…" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} max-w-sm`} />
        <div className="flex-1" />
        <a href={`${API}/api/admin/club/export.csv`} data-testid="club-export" className="h-9 px-3 inline-flex items-center gap-2 text-xs border border-brand-border hover:border-brand-accent hover:text-brand-accent"><Download size={13} /> Export CSV</a>
      </div>
      <table className="w-full text-sm">
        <thead><tr className="text-[10px] uppercase tracking-[0.2em] text-brand-muted border-b border-brand-border">
          <th className="text-left px-3 py-2">Číslo</th><th className="text-left px-3 py-2">Člen</th><th className="text-left px-3 py-2">Pobočka</th><th className="text-left px-3 py-2">Vozidlo</th><th className="text-right px-3 py-2">Body</th><th className="text-right px-3 py-2">Poptávky / hotovo</th><th className="text-right px-3 py-2">Uhrazeno</th><th className="text-left px-3 py-2">Odběr</th>
        </tr></thead>
        <tbody>
          {items.length === 0 && <tr><td colSpan={8} className="px-3 py-8 text-center text-brand-muted">Žádní členové.</td></tr>}
          {items.map((m) => (
            <tr key={m.id} onClick={() => onOpen(m.id)} className="border-b border-brand-border/60 last:border-0 cursor-pointer hover:bg-brand-bg/60" data-testid={`member-row-${m.member_number}`}>
              <td className="px-3 py-2 font-mono text-brand-accent">{m.member_number}</td>
              <td className="px-3 py-2"><div className="font-semibold">{m.name}</div><div className="text-[11px] text-brand-muted">{m.email}{m.phone && ` · ${m.phone}`}</div></td>
              <td className="px-3 py-2">{loc(m.preferred_location)}</td>
              <td className="px-3 py-2 text-brand-muted">{m.vehicle?.make_model || "—"}{m.vehicle?.plate && ` · ${m.vehicle.plate}`}</td>
              <td className="px-3 py-2 text-right font-mono font-bold text-brand-accent" data-testid={`member-points-${m.member_number}`}>{m.points ?? 0}</td>
              <td className="px-3 py-2 text-right">{m.reservations} / {m.done}</td>
              <td className="px-3 py-2 text-right text-brand-accent">{czk(m.paid || 0)}</td>
              <td className="px-3 py-2 text-xs">{m.marketing_opt_in ? "ano" : "ne"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Offers() {
  const [items, setItems] = useState([]);
  const load = () => adminApi.get("/admin/club/offers").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const save = async (o) => {
    try {
      await adminApi.put(`/admin/club/offers/${o.id}`, { title: o.title, description: o.description, conditions: o.conditions, active: o.active, valid_from: o.valid_from || null, valid_to: o.valid_to || null, max_redemptions_per_member: Number(o.max_redemptions_per_member) });
      toast.success("Nabídka uložena.");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  const up = (id, k, v) => setItems((l) => l.map((o) => (o.id === id ? { ...o, [k]: v } : o)));
  return (
    <div className="grid lg:grid-cols-2 gap-3" data-testid="club-offers">
      {items.map((o) => (
        <div key={o.id} className="bg-brand-surface border border-brand-border p-4 space-y-2" data-testid={`admin-offer-${o.key}`}>
          <div className="flex items-center justify-between gap-2">
            <Input value={o.title} onChange={(e) => up(o.id, "title", e.target.value)} className={`${inputCls} font-semibold`} />
            <span className={`text-[10px] uppercase tracking-[0.2em] px-2 py-1 border ${o.live ? "border-brand-accent text-brand-accent" : "border-brand-border text-brand-muted"}`} data-testid={`offer-live-${o.key}`}>{o.live ? "Veřejně" : "Skryto"}</span>
          </div>
          <Input value={o.description} onChange={(e) => up(o.id, "description", e.target.value)} placeholder="Popis" className={inputCls} />
          <textarea value={o.conditions} onChange={(e) => up(o.id, "conditions", e.target.value)} placeholder="Podmínky (bez nich se nabídka veřejně nezobrazí) — výši slevy/odměny doplňte zde" rows={3} data-testid={`offer-conditions-${o.key}`}
            className="w-full bg-brand-bg border border-brand-border text-white text-sm p-2 resize-none" />
          <div className="grid grid-cols-3 gap-2 text-xs">
            <label className="text-brand-muted">Od<Input type="date" value={o.valid_from || ""} onChange={(e) => up(o.id, "valid_from", e.target.value)} className={inputCls} /></label>
            <label className="text-brand-muted">Do<Input type="date" value={o.valid_to || ""} onChange={(e) => up(o.id, "valid_to", e.target.value)} className={inputCls} /></label>
            <label className="text-brand-muted">Čerpání / člen<Input type="number" min={0} value={o.max_redemptions_per_member} onChange={(e) => up(o.id, "max_redemptions_per_member", e.target.value)} className={inputCls} /></label>
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid={`offer-active-${o.key}`} checked={o.active} onChange={(e) => up(o.id, "active", e.target.checked)} className="accent-[#00DF82]" /> Aktivní</label>
            <span className="text-xs text-brand-muted">Čerpáno {o.redemptions}×</span>
            <button onClick={() => save(o)} data-testid={`offer-save-${o.key}`} className="h-8 px-3 text-xs bg-brand-accent text-black font-semibold">Uložit</button>
          </div>
        </div>
      ))}
    </div>
  );
}

function Referral() {
  const [r, setR] = useState({ enabled: false, rules: "" });
  useEffect(() => { adminApi.get("/admin/club/referral").then((x) => setR(x.data)).catch(() => {}); }, []);
  const save = async () => {
    try { setR((await adminApi.put("/admin/club/referral", r)).data); toast.success("Uloženo."); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  return (
    <div className="bg-brand-surface border border-brand-border p-4 space-y-2" data-testid="club-referral">
      <div className="flex items-center justify-between"><div className="font-semibold">Doporučovací program</div><span className="text-xs text-brand-muted">{r.enabled ? "zapnuto" : "vypnuto — čeká na pravidla"}</span></div>
      <textarea value={r.rules} onChange={(e) => setR({ ...r, rules: e.target.value })} rows={2} placeholder="Pravidla odměny za doporučení (kdo, kolik, kdy). Bez pravidel nelze zapnout." className="w-full bg-brand-bg border border-brand-border text-white text-sm p-2 resize-none" data-testid="referral-rules" />
      <div className="flex items-center justify-between">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" data-testid="referral-enabled" checked={r.enabled} onChange={(e) => setR({ ...r, enabled: e.target.checked })} className="accent-[#00DF82]" /> Zapnout</label>
        <button onClick={save} data-testid="referral-save" className="h-8 px-3 text-xs bg-brand-accent text-black font-semibold">Uložit</button>
      </div>
    </div>
  );
}

function Leads() {
  const [st, setSt] = useState(null);
  const [q, setQ] = useState("");
  const [segment, setSegment] = useState("");
  const [service, setService] = useState("");
  const [sort, setSort] = useState("recent");
  const [busy, setBusy] = useState("");
  const load = (query = q) => adminApi.get("/admin/club/leads", { params: { q: query, segment, service, sort } }).then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { const t = setTimeout(() => load(q), 250); return () => clearTimeout(t); }, [q, segment, service, sort]); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async (key, fn, okMsg) => {
    setBusy(key);
    try { const r = await fn(); toast.success(typeof okMsg === "function" ? okMsg(r) : okMsg); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(""); }
  };
  const upload = (file) => file && run("import", async () => {
    const fd = new FormData(); fd.append("file", file);
    return (await adminApi.post("/admin/club/import-leads", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
  }, (r) => `Načteno ${r.rows} řádků · nových ${r.created}, aktualizováno ${r.updated}, neplatný e-mail ${r.invalid_email} · do SmartEmailingu ${r.synced_to_smartemailing ?? 0}${r.smartemailing_error ? ` · SmartEmailing: ${r.smartemailing_error}` : ""}.`);
  const sendOwner = () => run("owner", async () => (await adminApi.post("/admin/club/invitation/send-to-owner")).data, (r) => `Kontrolní pozvánka odeslána na ${r.to}.`);
  const sendAll = () => {
    if (!window.confirm(`Odeslat pozvánku do klubu přes SmartEmailing VŠEM kontaktům v seznamu (${st?.valid_email ?? 0} leadů + zákazníci z poptávek)?`)) return;
    run("send", async () => (await adminApi.post("/admin/smartemailing/send-club-invitation")).data, (r) => `Pozvánka do klubu odeslána (kampaň č. ${r.campaign_id}, příjemců ${r.recipients ?? "—"}).`);
  };
  const del = (l) => window.confirm(`Smazat lead ${l.name || l.email}?`) && run("del", async () => (await adminApi.delete(`/admin/club/leads/${l.id}`)).data, "Lead smazán.");
  const sendSegment = () => {
    const validShown = (st?.items || []).filter((i) => i.email_valid).length;
    if (!validShown) return toast.error("V tomto výběru nejsou žádné kontakty s platným e-mailem.");
    const segLabel = segment ? SEG[segment].label : "Všichni";
    const svc = service ? ` · služba: ${svcLabel(service)}` : "";
    if (!window.confirm(`Odeslat nabídku přes SmartEmailing výběru „${segLabel}"${svc} — přibližně ${validShown} příjemců? Akce je nevratná.`)) return;
    run("segment", async () => (await adminApi.post("/admin/smartemailing/send-to-segment", { segment, service })).data,
      (r) => `Nabídka odeslána výběru „${r.segment_label}" (kampaň č. ${r.campaign_id}, příjemců ${r.targeted}).`);
  };

  const btn = "h-9 px-3 border border-brand-border hover:border-brand-accent hover:text-brand-accent text-xs inline-flex items-center gap-2 disabled:opacity-50";
  return (
    <div className="bg-brand-surface border border-brand-accent/40" data-testid="club-leads">
      <div className="p-4 flex flex-wrap items-start justify-between gap-3 border-b border-brand-border">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted">Leady (starší databáze) · pozvánka do klubu</div>
          <div className="mt-1.5 font-display font-black text-2xl" data-testid="leads-total">{st ? st.total : "—"} <span className="text-base font-normal text-brand-muted">leadů · {st?.valid_email ?? "—"} s platným e-mailem · {st?.already_members ?? "—"} už členy</span></div>
          <div className="text-xs text-brand-muted mt-1">Leady se nepřevádí na členy automaticky — dostanou pozvánku a vstoupí sami.</div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <label className={`${btn} cursor-pointer`}><Upload size={13} /> {busy === "import" ? "Nahrávám..." : "Nahrát CSV / XLSX"}
            <input type="file" accept=".csv,.xlsx,text/csv" className="hidden" data-testid="leads-file" onChange={(e) => upload(e.target.files?.[0])} /></label>
          <a href={`${API}/api/admin/club/leads-export.csv`} data-testid="leads-export" className={btn}><Download size={13} /> Export leadů</a>
          <a href={`${API}/api/admin/club-invitation-preview`} target="_blank" rel="noreferrer" data-testid="leads-invitation-preview" className={btn}>Náhled pozvánky</a>
          <button onClick={sendOwner} disabled={!!busy} data-testid="leads-send-owner" className={`${btn} border-brand-accent text-brand-accent`}>{busy === "owner" ? "Odesílám..." : "Poslat pozvánku na můj e-mail"}</button>
          <button onClick={sendAll} disabled={!!busy || !st} data-testid="leads-send-invitation" className="h-9 px-4 bg-brand-accent text-black text-xs font-bold hover:bg-brand-accentHover disabled:opacity-50">{busy === "send" ? "Odesílám..." : "Odeslat pozvánku do klubu všem"}</button>
        </div>
      </div>
      {st?.campaigns?.length > 0 && (
        <div className="px-4 py-2 border-b border-brand-border text-xs text-brand-muted" data-testid="leads-campaigns">
          Odeslané pozvánky do klubu: {st.campaigns.map((c) => `#${c.campaign_id} (${new Date(c.created_at).toLocaleString("cs-CZ")}${c.recipients ? `, ${c.recipients} příjemců` : ""})`).join(" · ")}
        </div>
      )}
      <div className="p-3 border-b border-brand-border space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Search size={14} className="text-brand-muted" />
          <Input data-testid="leads-search" placeholder="Hledat lead (jméno, e-mail, telefon)…" value={q} onChange={(e) => setQ(e.target.value)} className={`${inputCls} max-w-xs`} />
          <select data-testid="leads-service" value={service} onChange={(e) => setService(e.target.value)} className={`${inputCls} px-2`}>
            <option value="">Všechny služby</option>
            {(st?.services || []).map((s) => <option key={s} value={s}>{svcLabel(s)}</option>)}
          </select>
          <select data-testid="leads-sort" value={sort} onChange={(e) => setSort(e.target.value)} className={`${inputCls} px-2`}>
            <option value="recent">Nejnovější</option>
            <option value="extras">Nejvíce služeb navíc</option>
            <option value="name">Podle jména (A–Z)</option>
          </select>
          <span className="text-xs text-brand-muted ml-auto">{st?.items?.length ?? 0} zobrazeno</span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {[["", "Vše", st?.total], ["active", "Aktivní kupující", st?.segment_counts?.active], ["buyer", "Kupující", st?.segment_counts?.buyer], ["base", "Nenakupují", st?.segment_counts?.base]].map(([k, label, cnt]) => (
            <button key={k || "all"} onClick={() => setSegment(k)} data-testid={`leads-seg-${k || "all"}`}
              className={`h-8 px-3 text-xs border transition-colors ${segment === k ? "bg-brand-accent text-black border-brand-accent font-semibold" : "border-brand-border text-brand-muted hover:border-brand-accent hover:text-brand-accent"}`}>
              {label}{cnt != null ? ` · ${cnt}` : ""}
            </button>
          ))}
          <button onClick={sendSegment} disabled={!!busy || !(st?.items || []).some((i) => i.email_valid)} data-testid="leads-send-segment"
            className="ml-auto h-8 px-3 text-xs font-bold bg-brand-accent text-black hover:bg-brand-accentHover disabled:opacity-50 inline-flex items-center gap-1.5">
            <Send size={12} /> {busy === "segment" ? "Odesílám…" : "Odeslat nabídku výběru"}
          </button>
        </div>
      </div>
      <div className="max-h-[420px] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 bg-brand-surface"><tr className="text-[10px] uppercase tracking-[0.2em] text-brand-muted border-b border-brand-border">
            <th className="text-left px-3 py-2">Lead</th><th className="text-left px-3 py-2">Telefon</th><th className="text-left px-3 py-2">Pobočka</th><th className="text-left px-3 py-2">Vůz</th><th className="text-left px-3 py-2">Služby navíc</th><th className="text-left px-3 py-2">Poptávka</th><th className="text-left px-3 py-2">Stav</th><th></th>
          </tr></thead>
          <tbody>
            {(st?.items || []).map((l) => (
              <tr key={l.id} className="border-b border-brand-border/60 last:border-0" data-testid={`lead-row-${l.id}`}>
                <td className="px-3 py-2"><div className="font-semibold flex items-center gap-2 flex-wrap">{l.name || "—"} <span className={`text-[9px] uppercase tracking-wide px-1.5 py-0.5 ${segOf(l).cls}`} data-testid={`lead-segment-${l.id}`}>{segOf(l).label}</span></div><div className={`text-[11px] ${l.email_valid ? "text-brand-muted" : "text-red-400"}`}>{l.email || "bez e-mailu"}</div></td>
                <td className="px-3 py-2 text-brand-muted"><a href={`tel:${l.phone}`} className="hover:text-brand-accent">{l.phone}</a></td>
                <td className="px-3 py-2">{loc(l.location)}</td>
                <td className="px-3 py-2 text-brand-muted">{l.vehicle || "—"}</td>
                <td className="px-3 py-2">{(l.services?.length) ? <div className="flex flex-wrap gap-1">{l.services.map((s, i) => <span key={i} className="text-[10px] px-1.5 py-0.5 bg-brand-bg border border-brand-border text-white/80">{svcLabel(s)}</span>)}</div> : <span className="text-brand-muted text-xs">—</span>}</td>
                <td className="px-3 py-2 text-brand-muted text-xs">{l.created_at ? new Date(l.created_at).toLocaleDateString("cs-CZ") : "—"}</td>
                <td className="px-3 py-2 text-xs">{l.is_member ? <span className="text-brand-accent">člen</span> : l.email_valid ? "pozvat" : <span className="text-red-400">jen telefon</span>}</td>
                <td className="px-3 py-2 text-right"><button onClick={() => del(l)} data-testid={`lead-delete-${l.id}`} className="text-xs text-brand-muted hover:text-red-400">Smazat</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Redemptions() {
  const [offers, setOffers] = useState([]);
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ member_number: "", offer_id: "", note: "" });
  const load = () => Promise.all([adminApi.get("/admin/club/offers"), adminApi.get("/admin/club/redemptions")]).then(([o, r]) => { setOffers(o.data); setItems(r.data); }).catch(() => {});
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try {
      const ms = (await adminApi.get("/admin/club/members", { params: { q: f.member_number.trim() } })).data;
      const m = ms.find((x) => x.member_number.toLowerCase() === f.member_number.trim().toLowerCase() || x.email === f.member_number.trim().toLowerCase());
      if (!m) return toast.error("Člen nenalezen — zadejte členské číslo nebo e-mail.");
      await adminApi.post("/admin/club/redemptions", { member_id: m.id, offer_id: f.offer_id, note: f.note });
      toast.success(`Čerpání zaznamenáno pro ${m.member_number}.`);
      setF({ member_number: "", offer_id: "", note: "" }); load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  return (
    <div className="bg-brand-surface border border-brand-border p-4 space-y-3" data-testid="club-redemptions">
      <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted">Evidence využití výhod</div>
      <div className="grid sm:grid-cols-4 gap-2">
        <Input data-testid="redeem-member" placeholder="Členské číslo nebo e-mail" value={f.member_number} onChange={(e) => setF({ ...f, member_number: e.target.value })} className={inputCls} />
        <select data-testid="redeem-offer" value={f.offer_id} onChange={(e) => setF({ ...f, offer_id: e.target.value })} className="h-9 bg-brand-bg border border-brand-border text-white text-sm px-2">
          <option value="">Vyberte výhodu…</option>
          {offers.map((o) => <option key={o.id} value={o.id}>{o.title}{o.live ? "" : " (skrytá)"}</option>)}
        </select>
        <Input data-testid="redeem-note" placeholder="Poznámka (např. návštěva 30. 9.)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className={inputCls} />
        <button onClick={submit} disabled={!f.member_number || !f.offer_id} data-testid="redeem-submit" className="h-9 bg-brand-accent text-black text-xs font-semibold disabled:opacity-50">Zaznamenat čerpání</button>
      </div>
      <div className="max-h-48 overflow-auto text-sm">
        {items.length === 0 ? <div className="text-brand-muted text-xs">Zatím žádné čerpání.</div> : items.map((r) => (
          <div key={r.id} className="flex justify-between gap-3 py-1.5 border-b border-brand-border/60 last:border-0"><span><span className="text-brand-accent font-mono">{r.member_number}</span> · {r.offer_title}{r.note && <span className="text-brand-muted"> · {r.note}</span>}</span><span className="text-brand-muted text-xs">{new Date(r.at).toLocaleString("cs-CZ")}</span></div>
        ))}
      </div>
    </div>
  );
}

function ScanSection({ onCode, busy }) {
  return (
    <div className="bg-brand-surface border border-brand-accent/40 p-4 space-y-3" data-testid="club-scan">
      <div className="flex items-center gap-2">
        <QrCode size={14} className="text-brand-accent" />
        <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted">Skener členských karet · body a odměny</div>
        {busy && <span className="text-xs text-brand-muted">Načítám člena…</span>}
      </div>
      <CardScanner onCode={onCode} busy={busy} />
      <div className="text-xs text-brand-muted">Naskenujte QR kód z členské karty (PDF / telefon zákazníka) — otevře se profil člena, kde přidáte body, zapíšete návštěvu nebo uplatníte odměnu. Kliknutím na řádek v seznamu členů otevřete profil ručně.</div>
    </div>
  );
}

export function ClubPanel({ scanCode, onScanConsumed }) {
  const [profile, setProfile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [version, setVersion] = useState(0);

  const openByCode = useCallback(async (code) => {
    setBusy(true);
    try { setProfile((await adminApi.get(`/admin/club/scan/${encodeURIComponent(code)}`)).data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  }, []);
  const openById = async (id) => {
    try { setProfile((await adminApi.get(`/admin/club/members/${id}/profile`)).data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { if (scanCode) { openByCode(scanCode); onScanConsumed?.(); } }, [scanCode, openByCode, onScanConsumed]);

  return (
    <div className="space-y-6" data-testid="club-panel">
      <ClubStats />
      <ScanSection onCode={openByCode} busy={busy} />
      <MemberProfile data={profile} onChange={(d) => { setProfile(d); setVersion((v) => v + 1); }} onClose={() => setProfile(null)} />
      <RewardsPanel />
      <Leads />
      <Members onOpen={openById} refreshKey={version} />
      <Redemptions />
      <div className="grid lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2"><Offers /></div>
        <Referral />
      </div>
    </div>
  );
}
